import React, { useEffect, useState } from 'react'
import { useWallet } from '../../contexts/WalletContext'
import { getAffiliateMembers, getAffiliateCommissionHistory } from '../../services/affiliateService'
import type { AffiliateMemberItem, AffiliateCommissionHistoryItem } from '../../services/affiliateService'

function formatJoinDate(joinedAt: string): string {
  try {
    const d = new Date(joinedAt)
    return isNaN(d.getTime()) ? joinedAt : d.toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return joinedAt
  }
}

function formatHistoryDate(item: AffiliateCommissionHistoryItem): string {
  if (item.date) return item.date
  if (item.createdAt) {
    const d = new Date(item.createdAt)
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')
  }
  return '–'
}

function maskMember(raw: string): string {
  const s = String(raw || '').trim()
  if (!s) return 'สมาชิก'
  if (s.length < 4) return s
  if (/^\d+$/.test(s)) {
    if (s.length >= 10) return s.slice(0, 3) + 'xxxxxx' + s.slice(-1)
    return s.slice(0, 2) + 'xxxxx' + s.slice(-1)
  }
  if (s.length <= 6) return s.slice(0, 1) + 'xxx' + s.slice(-1)
  return s.slice(0, 2) + '****' + s.slice(-3)
}

const AffiliateIncomePage: React.FC = () => {
  const { formatCurrency } = useWallet()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [members, setMembers] = useState<AffiliateMemberItem[]>([])
  const [commissionHistory, setCommissionHistory] = useState<AffiliateCommissionHistoryItem[]>([])

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const [list, history] = await Promise.all([
          getAffiliateMembers(),
          getAffiliateCommissionHistory(30)
        ])
        if (!cancelled) {
          setMembers(list)
          setCommissionHistory(history)
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  if (loading) {
    return (
      <div className="text-center py-3">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2">กำลังโหลดข้อมูล...</p>
      </div>
    )
  }
  if (error) {
    return (
      <div className="text-center py-3">
        <p className="c-red">{error}</p>
      </div>
    )
  }

  return (
    <div>
      <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium mb-2">
        <i className="far fa-plus-square me-2"></i>
        รายได้
      </div>

      {commissionHistory.length > 0 && (
        <div className="bdr-5 bgc-4 p-2 mb-2">
          <small className="t-medium">ประวัติคอมมิชชัน</small>
          <div className="table-responsive w-100 mt-1">
            <table className="table mb-0 table-striped table-bordered text-center table-sm">
              <thead className="table-dark">
                <tr>
                  <th>วันที่</th>
                  <th>ยอดเสียสมาชิก</th>
                  <th>คอมมิชชั่น</th>
                </tr>
              </thead>
              <tbody>
                {commissionHistory.map((item, i) => (
                  <tr key={i}>
                    <td>{formatHistoryDate(item)}</td>
                    <td>{formatCurrency(item.betAmount ?? 0)}</td>
                    <td>{formatCurrency(item.commission ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bdr-5 bgc-4 p-2">
        <div className="row gap-2 mx--1">
          {members.length === 0 ? (
            <div className="col-12 px-1">
              <h5 className="c-red text-center">ไม่มีข้อมูลสมาชิกที่ชวน</h5>
            </div>
          ) : (
            members.map((member, index) => (
              <div key={member.username + index} className="col-12 px-1">
                <div className="bdr-5 bdc-2 bgc-4 p-2">
                  <div className="wrap c-red t-medium justify-content-between">
                    <h6 className="">{maskMember(member.username || member.displayName || 'สมาชิก')}</h6>
                    <h6 className="">No. {index + 1}</h6>
                  </div>

                  <small>
                    สมัครเมื่อ: {formatJoinDate(member.joinedAt)} - 
                    <span className={member.status === 'active' ? 'c-green' : 'c-red'}> {member.status || 'สมาชิกปกติ'}</span>
                  </small>

                  <div
                    className="d-grid gap-2 mt-1"
                    style={{
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
                    }}
                  >
                    <div className="bgg-1 bdr-5 bdc-1 p-1 text-center">
                      <div className="bgc-4 px-1 py-1 t-regular bdr-5">
                        <small>เงินเดิมพันทั้งหมด</small>
                      </div>
                      <div className="px-1 t-regular c-white">
                        <h4>{formatCurrency(0)}</h4>
                      </div>
                    </div>

                    <div className="bgg-1 bdr-5 bdc-1 p-1 text-center">
                      <div className="bgc-4 px-1 py-1 t-regular bdr-5">
                        <small>สรุปผลแพ้/ชนะ</small>
                      </div>
                      <div className="px-1 t-regular c-white">
                        <h4>{formatCurrency(0)}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

export default AffiliateIncomePage
